import { useState } from 'react'
import { ArrowLeft, ChevronDown, FileSpreadsheet, Upload } from 'lucide-react'
import { downloadCsvTemplate } from '@/lib/csv'
import CsvColumnGuide from '@/components/CsvColumnGuide'

export default function OnboardingStepExistingClient({ onBack, onImport }) {
  const [isDragging, setIsDragging] = useState(false)

  const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files')

  const handleDragEnter = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (hasFiles(event)) setIsDragging(true)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
  }

  const handleDragLeave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (!event.currentTarget.contains(event.relatedTarget)) setIsDragging(false)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    const files = Array.from(event.dataTransfer?.files || [])
    if (files.length > 0) onImport(files)
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-balance text-2xl font-semibold text-foreground">Add Your Client&rsquo;s Employees</h1>
        <p className="text-sm text-muted-foreground">Import a payroll or recordkeeper file. You&rsquo;ll review it before anything changes.</p>
        <p className="text-xs text-muted-foreground">Your employee data never leaves this browser.</p>
      </div>

      <div className="space-y-3">
        <section className="rounded-xl border border-border bg-card/80 p-5">
          <div>
            <h2 className="font-semibold text-foreground">Upload an Employee File</h2>
            <p className="mt-1 text-sm text-muted-foreground">Upload a CSV or Excel export from payroll or your recordkeeper.</p>
          </div>
          <div
            role="region"
            aria-label="Employee file drop zone"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={[
              'mt-4 flex min-h-44 flex-col items-center justify-center rounded-lg border-2 border-dashed px-5 py-7 text-center transition-colors',
              isDragging
                ? 'border-primary bg-primary/10'
                : 'border-border bg-background/70 hover:border-primary/50 hover:bg-accent/40',
            ].join(' ')}
          >
            <span className={[
              'flex h-11 w-11 items-center justify-center rounded-full transition-colors',
              isDragging ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary',
            ].join(' ')} aria-hidden="true">
              <Upload className="h-5 w-5" />
            </span>
            <p className="mt-3 text-sm font-semibold text-foreground" aria-live="polite">
              {isDragging ? 'Release to review your employees' : 'Drag and drop your employee file here'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">CSV, TSV, XLS, or XLSX</p>
            <div className="mt-4 flex items-center gap-3">
              <span className="h-px w-8 bg-border" aria-hidden="true" />
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">or</span>
              <span className="h-px w-8 bg-border" aria-hidden="true" />
            </div>
            <button
              type="button"
              onClick={() => onImport()}
              className="mt-4 inline-flex h-9 items-center gap-2 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              Choose Employee File
            </button>
            <p className="mt-3 text-xs text-muted-foreground">
              Files open in a review step before replacing the current employee list.
            </p>
          </div>
        </section>

        <details className="group rounded-xl border border-border bg-card/80">
          <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 text-left marker:content-none">
            <span>
              <span className="block text-sm font-semibold text-foreground">Need a CSV Template?</span>
              <span className="mt-1 block text-xs text-muted-foreground">Download the preferred columns and formatting guide.</span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
          </summary>
          <div className="space-y-4 border-t border-border px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs leading-relaxed text-muted-foreground">
                Starting fresh? Download a ready-to-fill file with every supported header.
              </p>
              <button
                type="button"
                onClick={downloadCsvTemplate}
                className="inline-flex h-9 shrink-0 touch-manipulation items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <FileSpreadsheet className="h-4 w-4 text-primary" aria-hidden="true" />
                Download CSV Template
              </button>
            </div>
            <CsvColumnGuide id="onboarding-column-guide" />
          </div>
        </details>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to Choices
        </button>
      </div>
    </div>
  )
}
